import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import toast from 'react-hot-toast';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faCoins, faSync } from '@fortawesome/free-solid-svg-icons';
import blockLinkUtils from '../utils/blocklink';
import { copyToClipboardWithToast } from '../utils/clipboard';
import './UniswapV2.css';
import { PeriodReturnChart, PnlSnapshot } from '../components/PeriodReturnChart';
import { ChainIcon } from '../components/ChainIcon';

interface TokenDetail {
    address: string;
    symbol: string;
    decimals: number;
    reserve: string;
    usd_price: number;
}

interface PnlData {
    level: string;
    pnl_snapshots: PnlSnapshot[];
}

interface PoolDetail {
    pool_id: string;
    chain: string;
    protocol: string;
    tvl_usd: number;
    day_usd_ratio: number;
    week_usd_ratio: number;
    fee_day_ratio: number;
    fee_week_ratio: number;
    tokens: TokenDetail[];
    pnls?: PnlData[];
    pool_data?: {
        fee?: number;
        total_supply?: string;
        A?: number | string;
    };
    updated_at: string;
}

interface ApiResponse {
    code: number;
    message: string;
    data: PoolDetail;
}

export function CurveStable() {
    const { chain, pool_id } = useParams();
    const [pool, setPool] = useState<PoolDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        if (!chain || !pool_id) return;

        setLoading(true);
        fetch(`/web3_liquidity/api/v1/liquidity_pool?chain=${chain}&pool_id=${pool_id}`)
            .then(res => res.json())
            .then((data: ApiResponse) => {
                if (data.code === 0) {
                    setPool(data.data);
                } else {
                    console.error("API Error:", data.message);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error("Fetch Error:", err);
                setLoading(false);
            });
    }, [chain, pool_id, refreshTrigger]);

    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
    };

    const formatNumber = (val: number, decimals: number = 4) => {
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: decimals }).format(val);
    };

    const formatPercent = (val: number) => {
        if (val === 0) return '-';
        return (val * 100 - 100).toFixed(4) + '%';
    };

    const formatFeePercent = (val: number) => {
        return (val * 100).toFixed(4) + '%';
    };

    const getYieldClass = (val: number) => {
        if (val > 1) return 'yield-positive';
        if (val < 1) return 'yield-negative';
        return '';
    };

    const handleCopy = (text: string) => {
        copyToClipboardWithToast(text);
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
        toast.success('Refreshing data...', {
            style: {
                background: '#333',
                color: '#fff',
            },
            iconTheme: {
                primary: '#6366f1',
                secondary: '#333',
            },
        });
    };

    const getLiquidityLink = function (chain: string, protocol: string, address: string): string {
        const getCurveChain = (c: string) => {
            switch (c.toLowerCase()) {
                case 'eth':
                case 'ethereum':
                    return 'ethereum';
                case 'bnb':
                case 'bsc':
                    return 'bsc';
                case 'base':
                    return 'base';
                case 'robinhood':
                    return 'robinhood';
                case 'polygon':
                    return 'polygon';
                case 'optimism':
                    return 'optimism';
                case 'avalanche':
                    return 'avalanche';
                default:
                    return c.toLowerCase();
            }
        };
        return `https://curve.fi/#/${getCurveChain(chain)}/pools/${address}/deposit`;
    };

    if (loading || !pool) {
        return (
            <div className="uniswap-v2-page">
                <div className="loading-text">Loading Pool Data...</div>
            </div>
        );
    }

    const tokenTitle = pool.tokens.map(t => t.symbol).join(' / ');

    return (
        <div className="uniswap-v2-page">
            <div className="landing-background">
                <div className="orb orb-1"></div>
                <div className="orb orb-2"></div>
                <div className="grid-overlay"></div>
            </div>
            <header className="page-header">
                <button className="refresh-btn" onClick={handleRefresh} title="Refresh Data">
                    <FontAwesomeIcon icon={faSync} spin={loading} />
                </button>

                <div className="header-content">
                    <div className="pool-info">
                        <h1 className="pool-title">{tokenTitle}</h1>
                        <div className="pool-meta">
                            <ChainIcon chain={pool.chain} size={24} showLabel={false} />
                            <span className="protocol-badge">{pool.protocol}</span>
                        </div>
                    </div>

                    <div className="header-actions">
                        <a
                            href={getLiquidityLink(pool.chain, pool.protocol, pool.pool_id)}
                            target="_blank"
                            rel="noreferrer"
                            className="add-lp-btn"
                        >
                            <FontAwesomeIcon icon={faCoins} />
                            <span>Add Liquidity</span>
                        </a>
                    </div>
                </div>
            </header>

            <div className="grid-container">
                <div className="glass-card">
                    <h3 className="card-title">Pool Statistics</h3>
                    <div className="stat-row">
                        <span className="stat-label">TVL</span>
                        <span className="stat-value">{formatCurrency(pool.tvl_usd)}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">Protocol</span>
                        <span className="stat-value">{pool.protocol}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">Address</span>
                        <div className="stat-value pool-id-container">
                            <span
                                className="address-copy-trigger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(pool.pool_id);
                                }}
                                title="Click to copy address"
                            >
                                {pool.pool_id.substring(0, 6)}...{pool.pool_id.substring(pool.pool_id.length - 4)}
                            </span>
                            <a
                                href={blockLinkUtils.getProtocolLink(pool.chain, pool.protocol, pool.pool_id)}
                                target="_blank"
                                rel="noreferrer"
                                className="explorer-link"
                                style={{ width: '24px', height: '24px', borderRadius: '6px', fontSize: '0.8rem' }}
                            >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                            </a>
                        </div>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">24h USD</span>
                        <span className={`stat-value ${getYieldClass(pool.day_usd_ratio)}`}>
                            {formatPercent(pool.day_usd_ratio)}
                        </span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">7d USD</span>
                        <span className={`stat-value ${getYieldClass(pool.week_usd_ratio)}`}>
                            {formatPercent(pool.week_usd_ratio)}
                        </span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">24h Fee</span>
                        <span className={`stat-value ${getYieldClass(pool.fee_day_ratio + 1)}`}>
                            {formatFeePercent(pool.fee_day_ratio)}
                        </span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">7d Fee</span>
                        <span className={`stat-value ${getYieldClass(pool.fee_week_ratio + 1)}`}>
                            {formatFeePercent(pool.fee_week_ratio)}
                        </span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">Fee Tier</span>
                        <span className="stat-value">
                            {pool.pool_data?.fee !== undefined
                                ? (pool.pool_data.fee > 1 ? `${pool.pool_data.fee / 10000}%` : `${(pool.pool_data.fee * 100).toFixed(4)}%`)
                                : 'N/A'}
                        </span>
                    </div>
                    {pool.pool_data?.A !== undefined && (
                        <div className="stat-row">
                            <span className="stat-label">Amplification (A)</span>
                            <span className="stat-value">{pool.pool_data.A}</span>
                        </div>
                    )}
                </div>

                <div className="glass-card">
                    <h3 className="card-title">Token Composition</h3>
                    {pool.tokens.map(token => (
                        <div key={token.address} className="token-row">
                            <div className="token-info">
                                <span className="token-symbol">{token.symbol}</span>
                                <div className="pool-id-container">
                                    <span
                                        className="address-copy-trigger"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(token.address);
                                        }}
                                        title="Click to copy address"
                                    >
                                        {token.address.substring(0, 6)}...{token.address.substring(token.address.length - 4)}
                                    </span>
                                    <a
                                        href={blockLinkUtils.getAddressLink(pool.chain, token.address)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="explorer-link"
                                        style={{ width: '30px', height: '30px', borderRadius: '8px' }}
                                    >
                                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                                    </a>
                                </div>
                            </div>
                            <div className="token-reserves">
                                <span className="token-amount">
                                    {formatNumber(parseFloat(token.reserve) / Math.pow(10, token.decimals))}
                                </span>
                                <span className="token-usd">
                                    ${formatNumber(token.usd_price, 4)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {
                pool.pnls && pool.pnls.map((e, idx) => {
                    return (
                        <div key={idx} className="glass-card chart-section">
                            <h3 className="card-title">Period Return Chart</h3>
                            <PeriodReturnChart snapshots={e.pnl_snapshots || []} />
                        </div>
                    )
                })
            }
        </div>
    );
}
